import { useTranslation } from 'react-i18next'

import {
  AppFlexContainer,
  AppFlexItem,
  AppDivider,
  AppButton,
} from 'client/ui-building-blocks'

import { Filter } from 'cat-shared/utils'
import { formatNumber } from 'client/helpers'
import { getAccountStatus, isAccountFull } from 'client/services'
import { useAppContext, usePageStateContext, usePage } from 'client/hooks'
import { type TGetUserData } from 'client/api/useGetUser/useGetUser'

import Stat from './Stat/stat'
import ResultsByCategory from './ResultsByCategory/results-by-category'

type Props = {
  data: TGetUserData
  isProfileUserAuthUser: boolean
}

const Stats = ({ data, isProfileUserAuthUser }: Props) => {
  const { t } = useTranslation()

  const { authUser } = useAppContext()
  const { setBrowseQuestionsPageState } = usePageStateContext()
  const { navigateTo } = usePage()

  const { questionsTotal, questionsByUser, answersStats } = data?.data.count || {}

  const accountStatus = getAccountStatus(authUser)

  const onUserQuestionsClick = () => {
    setBrowseQuestionsPageState(() => ({
      filter: Filter.Created,
      category: 'any',
      search: '',
      pageNo: 1,
    }))
    navigateTo({ path: '/browse' })
  }

  const allAnswersCount = answersStats
    .map(stat => stat.count)
    .reduce((prevCount, currentCount) => {
      return prevCount + currentCount
    }, 0)

  const correctAnswersCount = answersStats
    .map(stat => stat.correctCount)
    .reduce((prevCount, currentCount) => {
      return prevCount + currentCount
    }, 0)

  const items = [
    {
      count: allAnswersCount,
      dictPath: 'profilePage.stats.answer',
      formatValue: (count: number) => formatNumber(count),
    },
  ]

  if (allAnswersCount > 0) {
    items.push(
      ...[
        {
          count: correctAnswersCount,
          dictPath: 'profilePage.stats.correctAnswers',
          formatValue: (count: number) => formatNumber(count),
        },
        {
          count: correctAnswersCount,
          dictPath: 'profilePage.stats.correctness',
          formatValue: (count: number) =>
            `${((count / allAnswersCount || 0) * 100).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`,
        },
        {
          count: allAnswersCount,
          dictPath: 'profilePage.stats.allAnswer',
          formatValue: (count: number) =>
            `${((count / questionsTotal) * 100).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`,
        },
      ]
    )
  }

  if (questionsByUser > 0) {
    items.push({
      count: questionsByUser,
      dictPath: 'profilePage.stats.question',
      formatValue: (count: number) => formatNumber(count),
    })
  }

  const displayGoToQuestionsButton =
    questionsByUser && isProfileUserAuthUser && isAccountFull(accountStatus)

  return (
    <>
      <AppDivider border size="large" />
      <AppFlexContainer justifyContent="center" columnGap="50px" rowGap="25px">
        {items.map(({ count, dictPath, formatValue }, i) => (
          <AppFlexItem key={i}>
            <Stat count={count} value={formatValue(count)} dictPath={dictPath} />
          </AppFlexItem>
        ))}
      </AppFlexContainer>
      {displayGoToQuestionsButton ? (
        <>
          <AppDivider size="medium" />
          <AppButton
            variant="success"
            text={t('profilePage.buttons.goToQuestions')}
            onClick={onUserQuestionsClick}
            position="center"
          />
        </>
      ) : null}
      <ResultsByCategory getUserData={data} />
    </>
  )
}

Stats.defaultProps = {
  data: {
    count: {
      questionsTotal: 0,
      questionsByUser: 0,
      answersStats: [],
    },
  },
}

export default Stats
